import React from 'react';
import { useController, ApplicationView } from '@aha-app/mvc';
import type AssistantViewController from '../AssistantView/AssistantViewController';
import styles from './ArtifactPanel.module.css';
import { AhaCopyButton } from 'components/common/reactifyWebComponent';
import { Page } from 'components/framework/models';
import { allowReactContentPreview } from '../utils/allowReactContentPreview';
import { PanelCloseIcon } from './PanelCloseIcon';
import ShareArtifactPopover from '../SharePopover/ShareArtifactPopover';
import { FROZEN_CHAT_ERROR } from 'components/Assistant/constants';
import Tool from '../../agent_tools/Tool';

export const CONTENT_TYPE_SUPPORTS_SOURCE = [
  'text/html',
  'text/javascript',
  'application/vnd.aha.react',
];

const ArtifactHeader = ({ toolCall }) => {
  const controller = useController<AssistantViewController>();
  const { artifactDisplayMode, size } = controller.state;

  const modeButtons = CONTENT_TYPE_SUPPORTS_SOURCE.includes(
    toolCall.content?.panel?.contentType || 'text/markdown'
  ) &&
    allowReactContentPreview(toolCall) && (
      <aha-button-group>
        <aha-button
          size='small'
          active={artifactDisplayMode === 'preview' ? true : undefined}
          onClick={() => controller.actionSetArtfactDisplayMode('preview')}
        >
          <i className='far fa-eye' /> Preview
        </aha-button>
        <aha-button
          size='small'
          active={artifactDisplayMode === 'source' ? true : undefined}
          onClick={() => controller.actionSetArtfactDisplayMode('source')}
        >
          <i className='far fa-code' /> Source
        </aha-button>
      </aha-button-group>
    );

  const approvalButtons = (
    <>
      {toolCall.status === 'needs_approval' && (
        <>
          <aha-button
            kind='default'
            size='small'
            {...(controller.model.isChatFrozen ? { disabled: true } : {})}
            onClick={() => controller.actionRejectToolCall(toolCall)}
          >
            {toolCall.content.rejectButton?.label || 'Ignore'}
          </aha-button>
          {controller.model.isChatFrozen && (
            <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>
          )}
          <aha-button
            kind='primary'
            size='small'
            {...(controller.model.isChatFrozen ? { disabled: true } : {})}
            onClick={() => controller.actionApproveToolCall(toolCall)}
          >
            {toolCall.content.actionButton?.label || 'Approve'}
          </aha-button>
          {controller.model.isChatFrozen && (
            <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>
          )}
        </>
      )}
      {toolCall.status === 'error' && (
        <>
          <aha-button
            kind='primary'
            size='mini'
            {...(controller.model.isChatFrozen ? { disabled: true } : {})}
            onClick={() => controller.actionApproveToolCall(toolCall)}
          >
            Try again
          </aha-button>
          {controller.model.isChatFrozen && (
            <aha-tooltip>{FROZEN_CHAT_ERROR}</aha-tooltip>
          )}
        </>
      )}
    </>
  );

  const openReportButton = toolCall.content?.panel?.contentType ===
    'application/vnd.aha.bookmark' && (
    <aha-button
      kind='gray'
      size='mini'
      onClick={() => {
        window.open(
          `/bookmarks/${toolCall.content.panel.body.replace(/[^0-9]/g, '')}`,
          '_blank'
        );
      }}
    >
      View report
    </aha-button>
  );

  const artifactData = JSON.parse(toolCall.content?.data || '{}') as Record<
    string,
    any
  >;

  const openNoteButton =
    artifactData.page_id &&
    (artifactData.page_id === 'loading' ? (
      <aha-button kind='icon' size='mini'>
        <i className='fa fa-regular fa-circle-notch fa-spin' />
      </aha-button>
    ) : (
      <aha-button
        kind='icon'
        size='mini'
        onClick={() => {
          const pageId = artifactData.page_id;
          window.open(Page.pathForId(pageId)!, '_blank');
        }}
      >
        <i className='far fa-arrow-up-right-from-square' />
        <aha-tooltip style={{ '--Tooltip__tooltip--min-width': 0 }}>
          Open note in new tab
        </aha-tooltip>
      </aha-button>
    ));

  let agentButtons: React.ReactNode | null = null;
  if (toolCall.status !== 'needs_approval' && toolCall.status !== 'error') {
    agentButtons = Tool.getToolActions(toolCall, controller.agent);
  }

  const copyButton = toolCall.content?.panel?.body &&
    toolCall.status === 'complete' && (
      <>
        <AhaCopyButton
          size='mini'
          getText={() => toolCall.content.panel.body}
        />
        <aha-tooltip style={{ '--Tooltip__tooltip--min-width': 0 }}>
          Copy to clipboard
        </aha-tooltip>
      </>
    );

  const shareArtifact = toolCall.artifact && (
    <ShareArtifactPopover
      artifact={toolCall.artifact}
      version={
        toolCall.content?.data
          ? (JSON.parse(toolCall.content.data) as any).version
          : undefined
      }
    />
  );

  return (
    <div className={styles.artifactHeader}>
      <div className={styles.artifactLeftControls}>
        <aha-button
          kind='icon'
          size='mini'
          onClick={() => controller.actionCloseArtifact()}
        >
          {['full-screen', 'full-page'].includes(size) ? (
            <PanelCloseIcon />
          ) : (
            <i className='far fa-xmark' />
          )}
        </aha-button>
        {modeButtons}
        {toolCall.status === 'preparing' && <aha-loading-dots />}
        <div className={styles.artifactName}>
          {toolCall.content.panel?.title}
        </div>
      </div>
      <div className={styles.artifactTools}>
        {approvalButtons}
        {agentButtons}
        {openReportButton}
        {openNoteButton}
        {!openReportButton && copyButton}
        {shareArtifact}
      </div>
    </div>
  );
};

export default ApplicationView(ArtifactHeader);
